import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

const TiptapDropdown = ({
  mode = 'dropdown', // 'dropdown' or 'options'
  icon,
  value,
  label,
  onChange,
  onSelect,
  isActive = false,
  disabled = false,
  className = '',
  showArrow = false,
  title,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionClick = (optionValue, optionLabel) => {
    if (mode === 'dropdown' && onChange) {
      onChange(optionValue)
    } else if (mode === 'options' && onSelect) {
      onSelect(optionValue)
    }
    setIsOpen(false)
  }

  const getDisplayValue = () => {
    if (mode === 'options') return null

    if (label) return label
    if (value) return value
    return 'Select...'
  }

  const getButtonClasses = () => {
    const baseClasses = `
      relative flex items-center p-2 m-1 rounded cursor-pointer
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `

    if (isActive) {
      return `${baseClasses} bg-primary-350 text-secondary`
    }

    return `${baseClasses} hover:bg-gray-200 text-grey-400`
  }

  return (
    <div className="relative" ref={dropdownRef} title={title}>
      <button
        type="button"
        onClick={handleToggle}
        onMouseDown={(e) => e.preventDefault()}
        className={getButtonClasses()}
        disabled={disabled}
      >
        {icon && <span className="flex items-center">{icon}</span>}

        {/* Display Value (only in dropdown mode) */}
        {mode === 'dropdown' && (
          <span className="ml-1 text-[16px] truncate min-w-0 max-w-[60px] ">
            {getDisplayValue()}
          </span>
        )}

        {mode === 'dropdown' && (
          <span className="ml-1 flex items-center">
            {isOpen ? (
              <ChevronUp size={14} className="text-gray-400" />
            ) : (
              <ChevronDown size={14} className="text-gray-400" />
            )}
          </span>
        )}

        {mode === 'options' && showArrow && (
          <span className="ml-1 flex items-center">
            {isOpen ? (
              <ChevronUp size={14} className="text-gray-400" />
            ) : (
              <ChevronDown size={14} className="text-gray-400" />
            )}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-max">
          <div className="py-1">
            {React.Children.map(children, (child) => {
              if (!React.isValidElement(child)) return child

              if (child.type === TiptapDropdown.Option) {
                return React.cloneElement(child, {
                  onClick: () =>
                    handleOptionClick(child.props.value, child.props.children),
                  isActive:
                    mode === 'dropdown'
                      ? child.props.value === value
                      : child.props.isActive,
                })
              }

              return child
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Option Component
TiptapDropdown.Option = ({
  value,
  children,
  onClick,
  isActive = false,
  className = '',
}) => {
  const isHighlight = isActive
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full text-left px-3 py-2 hover:bg-gray-100 
        flex items-center cursor-pointer text-primary-400
        ${isHighlight ? 'bg-primary-350 text-secondary' : ''}
        ${className}
      `}
    >
      <div className="px-3 py-2">{children}</div>
    </button>
  )
}

export default TiptapDropdown
